/**
 * `<NoteComposer>` — append-only note composer (Batch 30j-6).
 *
 * Phase 2.5 update: accepts `jdId` prop and passes it through to the
 * `onSubmit` handler so notes are scoped per-(candidate × JD).
 *
 * Textarea + char-count + "Add note" button. Position-locked at the top
 * of the Notes pane (HoD §5.1). NO Cmd/Ctrl+Enter shortcut (HoD §5.6),
 * NO autosize (HoD §5.2), NO clear button (HoD §5.8), NO draft autosave.
 *
 * State derivation (per HoD §5.4 + Architect §3.3):
 *   - empty / whitespace-only → button disabled
 *   - over 50000 chars → button disabled, count visible in reject color
 *   - mutation pending → textarea + button disabled, button text "Adding…"
 *
 * Optimistic-UX rollback: when the orchestrator's mutation errors, it
 * increments `errorKey`. The composer's `useEffect` detects the change
 * and restores the textarea content from `submittedContentRef`
 * (Architect §5.4 / AR-11).
 */
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type JSX,
} from "react";

import { Button } from "@/components/common/Button";

interface NoteComposerProps {
  /** Called with the trimmed content (and optional jdId) when user clicks "Add note". */
  onSubmit: (trimmedContent: string, jdId?: string) => void;
  /** Mutation isPending — drives button copy + textarea disabled. */
  isPending: boolean;
  /** Mutation isError flag — paired with `errorKey` for rollback. */
  isError: boolean;
  /**
   * Monotonically-changing key that flips on each mutation error.
   * The composer uses it to detect "a NEW error since the last one I
   * handled" and restore the textarea exactly once per error.
   */
  errorKey: number;
  /**
   * Phase 2.5: current JD context ID for per-(candidate × JD) note scoping.
   * When provided it is passed through to `onSubmit`.
   */
  jdId?: string;
}

const MAX_LEN = 50_000;
const NEAR_CAP_THRESHOLD = 45_000;

const NUMBER_FORMAT = new Intl.NumberFormat("en-US");

function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function NoteComposer({
  onSubmit,
  isPending,
  isError,
  errorKey,
  jdId,
}: NoteComposerProps): JSX.Element {
  const [content, setContent] = useState<string>("");
  const submittedContentRef = useRef<string>("");
  const handledErrorKeyRef = useRef<number>(errorKey);

  const trimmed = content.trim();
  const charCount = content.length;
  const isOverCap = charCount > MAX_LEN;
  const isEmpty = trimmed.length === 0;
  const isSubmitDisabled = isEmpty || isOverCap || isPending;
  const nearCap = charCount >= NEAR_CAP_THRESHOLD && !isOverCap;
  const charCountVisible = nearCap || isOverCap;

  const charCountLabel = `${NUMBER_FORMAT.format(charCount)} / ${NUMBER_FORMAT.format(MAX_LEN)}`;

  // Restore the textarea content on a NEW mutation error since the last
  // one we handled. Architect §5.4 / AR-11.
  useEffect(() => {
    if (isError && errorKey !== handledErrorKeyRef.current) {
      setContent(submittedContentRef.current);
      handledErrorKeyRef.current = errorKey;
    }
  }, [isError, errorKey]);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    submittedContentRef.current = trimmed;
    setContent("");
    onSubmit(trimmed, jdId);
  };

  return (
    <form
      className="flex flex-col gap-3 rounded-md border border-border-default bg-surface-card p-4"
      data-testid="note-composer"
      onSubmit={handleSubmit}
    >
      <label
        htmlFor="note-composer-textarea"
        className="text-2xs font-bold uppercase tracking-[0.12em] text-text-tertiary"
      >
        Add a note
      </label>
      <textarea
        id="note-composer-textarea"
        name="content"
        rows={4}
        placeholder="Capture context the AI can't see — references, gut checks, recruiter notes, follow-up reminders…"
        className={cn(
          "min-h-[96px] w-full resize-y rounded-md border border-border-default bg-surface-subtle p-3",
          "text-sm text-text-primary placeholder:text-text-tertiary",
          "focus-visible:outline-none focus-visible:border-border-focus focus-visible:shadow-focus",
          "disabled:opacity-60 disabled:cursor-not-allowed",
        )}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        maxLength={MAX_LEN}
        disabled={isPending}
        aria-describedby="note-composer-charcount"
        aria-invalid={isOverCap}
        data-testid="note-composer-textarea"
      />
      <div className="flex items-center justify-between">
        <span
          id="note-composer-charcount"
          aria-live="polite"
          data-testid="note-composer-charcount"
          className={cn(
            "text-xs",
            isOverCap
              ? "text-status-rejected-fg"
              : nearCap
                ? "text-status-warn-fg"
                : "text-text-tertiary invisible select-none",
          )}
        >
          {charCountVisible ? charCountLabel : "0 / 50,000"}
        </span>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitDisabled}
          data-testid="note-composer-submit"
        >
          {isPending ? "Adding…" : "Add note"}
        </Button>
      </div>
    </form>
  );
}
